import { useState, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAIKey } from '../../hooks/useAIKey';
import { useTextToSQL } from '../../lib/queries';

// Types
interface Message {
    id: string;
    role: 'user' | 'assistant' | 'error';
    content: string;
    sql?: string;
    data?: any[];
}

// ============================================================================
// Subcomponents (Tailwind / Inline Styles)
// ============================================================================

const ConfigureAIButton = ({ onConfigure }: { onConfigure: () => void }) => (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center text-slate-500">
        <div className="text-4xl mb-4">🤖</div>
        <h3 className="text-lg font-semibold text-slate-700 mb-2">配置 AI 功能</h3>
        <p className="text-sm text-slate-500 mb-6 max-w-xs mx-auto">
            使用自然语言查询您的账本数据。需要配置 OpenAI Compatible API Key。
        </p>
        <button
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition shadow-sm"
            onClick={onConfigure}
        >
            配置 API Key
        </button>
    </div>
);

const MessageList = ({ messages, isLoading }: { messages: Message[]; isLoading: boolean }) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isLoading]);

    return (
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.length === 0 && (
                <div className="text-center p-8 text-slate-400 text-sm">
                    <p>👋 我是 AI 助手，可以帮您查询账单。</p>
                    <p className="mt-2">试着问：<br />“上个月花了多少钱？”<br />“统计餐饮支出”</p>
                </div>
            )}

            {messages.map((msg) => (
                <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                    <div
                        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm ${msg.role === 'user'
                            ? 'bg-indigo-600 text-white rounded-br-none'
                            : msg.role === 'error'
                                ? 'bg-red-50 text-red-600 rounded-bl-none border border-red-100'
                                : 'bg-white text-slate-800 rounded-bl-none border border-slate-100'
                            }`}
                        style={{ wordBreak: 'break-word' }}
                    >
                        <div className="whitespace-pre-wrap">{msg.content}</div>
                        {msg.data && (
                            <div className="mt-3 overflow-x-auto bg-slate-50 rounded p-2 text-xs font-mono">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr>{Object.keys(msg.data[0] || {}).map(k => <th key={k} className="p-1 border-b border-slate-200">{k}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                        {msg.data.slice(0, 5).map((row, i) => (
                                            <tr key={i}>
                                                {Object.values(row).map((v: any, j) => <td key={j} className="p-1 border-b border-slate-100 text-slate-600">{String(v)}</td>)}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {msg.data.length > 5 && <p className="text-center mt-1 text-slate-400">...共 {msg.data.length} 条</p>}
                            </div>
                        )}
                    </div>
                </div>
            ))}

            {isLoading && (
                <div className="flex justify-start">
                    <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm">
                        <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                    </div>
                </div>
            )}
            <div ref={messagesEndRef} />
        </div>
    );
};

// ============================================================================
// Main Component
// ============================================================================

export function AIChatBox() {
    const { aiKey, apiBaseUrl, chatModel, embeddingModel, isConfigured, setAIKey, setApiBaseUrl, setChatModel, setEmbeddingModel, clearAIKey } = useAIKey();
    const [showModal, setShowModal] = useState(false);
    const [key, setKey] = useState("");
    const [baseUrl, setBaseUrl] = useState("");
    const [chatModelInput, setChatModelInput] = useState("");
    const [embeddingModelInput, setEmbeddingModelInput] = useState("");
    const [error, setError] = useState("");

    // Chat state
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState<Message[]>([]);
    const { mutateAsync: textToSQL, isPending } = useTextToSQL();

    // Init state from context when modal opens
    useEffect(() => {
        if (showModal) {
            setKey(aiKey || "");
            setBaseUrl(apiBaseUrl || "https://api.openai.com/v1");
            setChatModelInput(chatModel || "gpt-4o-mini");
            setEmbeddingModelInput(embeddingModel || "text-embedding-3-small");
        }
    }, [showModal, aiKey, apiBaseUrl, chatModel, embeddingModel]);

    const handleSave = () => {
        if (!key.trim()) {
            setError("请输入 API Key");
            return;
        }
        // Basic URL validation
        if (baseUrl && !baseUrl.startsWith("http")) {
            setError("Base URL 必须以 http:// 或 https:// 开头");
            return;
        }

        setAIKey(key);
        setApiBaseUrl(baseUrl || "https://api.openai.com/v1");
        setChatModel(chatModelInput || "gpt-4o-mini");
        setEmbeddingModel(embeddingModelInput || "text-embedding-3-small");
        setShowModal(false);
        setError("");
    };

    const handleSend = async () => {
        if (!input.trim() || isPending) return;

        const userMsg: Message = { id: crypto.randomUUID(), role: 'user', content: input.trim() };
        setMessages(prev => [...prev, userMsg]);
        setInput("");

        try {
            // Pass object format as expected by the hook
            const result = await textToSQL({ query: userMsg.content, execute: true });
            const res = result as any;

            if (res.success) {
                setMessages(prev => [...prev, {
                    id: crypto.randomUUID(),
                    role: 'assistant',
                    content: res.explanation || "查询成功",
                    data: res.data,
                    sql: res.generatedSQL
                }]);
            } else {
                // Handle structured error response
                const errorMsg = typeof res.error === 'string'
                    ? res.error
                    : res.error?.message || JSON.stringify(res.error) || "查询失败";
                setMessages(prev => [...prev, {
                    id: crypto.randomUUID(),
                    role: 'error',
                    content: errorMsg
                }]);
            }
        } catch (e: any) {
            // Handle thrown errors
            const errorMsg = typeof e === 'string' ? e : e?.message || JSON.stringify(e) || "请求发生错误";
            setMessages(prev => [...prev, {
                id: crypto.randomUUID(),
                role: 'error',
                content: errorMsg
            }]);
        }
    };

    const onClose = () => setShowModal(false);

    // Modal Content Render (Moved to a var for simple conditional rendering)
    const modalContent = createPortal(
        <div
            className="modal-overlay"
            style={{
                zIndex: 99999,
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
            onClick={onClose}
        >
            <form
                style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "16px",
                    padding: "24px",
                    width: "90%",
                    maxWidth: "400px",
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                    margin: "16px"
                }}
                onClick={(e) => e.stopPropagation()}
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0, color: '#111827' }}>配置 AI 功能</h2>
                    <button type="button" onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#9ca3af' }}>×</button>
                </div>

                <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
                    您的配置仅存储在本地浏览器中，不会发送到我们的服务器。
                </p>

                <div style={{ marginBottom: "20px" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 500, color: "#374151" }}>
                        API Base URL
                    </label>
                    <input
                        type="text"
                        name="baseUrl"
                        autoComplete="off"
                        placeholder="https://api.openai.com/v1"
                        value={baseUrl}
                        onChange={(e) => { setBaseUrl(e.target.value); setError(""); }}
                        style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            fontSize: "14px",
                            marginBottom: "4px"
                        }}
                    />
                    <p style={{ fontSize: "12px", color: "#9ca3af", margin: 0 }}>
                        支持 OpenAI、Azure、DeepSeek 等兼容接口
                    </p>
                </div>

                <div style={{ marginBottom: "24px" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 500, color: "#374151" }}>
                        API Key
                    </label>
                    <input
                        type="password"
                        name="apiKey"
                        autoComplete="new-password"
                        placeholder="sk-..."
                        value={key}
                        onChange={(e) => { setKey(e.target.value); setError(""); }}
                        style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            fontSize: "14px",
                        }}
                    />
                </div>

                <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 500, color: "#374151" }}>
                        Chat 模型
                    </label>
                    <input
                        type="text"
                        name="chatModel"
                        autoComplete="off"
                        placeholder="gpt-4o-mini"
                        value={chatModelInput}
                        onChange={(e) => { setChatModelInput(e.target.value); setError(""); }}
                        style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            fontSize: "14px",
                        }}
                    />
                    <p style={{ fontSize: "12px", color: "#9ca3af", margin: "4px 0 0" }}>
                        用于 AI 问答，如 gpt-4o-mini, deepseek-chat
                    </p>
                </div>

                <div style={{ marginBottom: "24px" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 500, color: "#374151" }}>
                        Embedding 模型
                    </label>
                    <input
                        type="text"
                        name="embeddingModel"
                        autoComplete="off"
                        placeholder="text-embedding-3-small"
                        value={embeddingModelInput}
                        onChange={(e) => { setEmbeddingModelInput(e.target.value); setError(""); }}
                        style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            fontSize: "14px",
                        }}
                    />
                    <p style={{ fontSize: "12px", color: "#9ca3af", margin: "4px 0 0" }}>
                        用于分类建议，如 text-embedding-3-small
                    </p>
                </div>

                {error && <p style={{ fontSize: '14px', color: '#ef4444', marginBottom: '16px', padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px' }}>{error}</p>}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: "#f3f4f6",
                            color: "#4b5563",
                            borderRadius: "8px",
                            border: "none",
                            fontSize: "14px",
                            cursor: "pointer"
                        }}
                    >
                        取消
                    </button>
                    <button
                        type="submit"
                        style={{
                            padding: "8px 16px",
                            backgroundColor: "#4f46e5",
                            color: "#ffffff",
                            borderRadius: "8px",
                            border: "none",
                            fontSize: "14px",
                            cursor: "pointer",
                            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
                        }}
                    >
                        保存
                    </button>
                </div>
            </form>
        </div>,
        document.body
    );

    return (
        <div className="flex flex-col h-[500px] bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden relative">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
                <div className="flex items-center space-x-2">
                    <span className="text-xl">🤖</span>
                    <h3 className="font-medium text-slate-800">AI 助手</h3>
                </div>
                {isConfigured && (
                    <button
                        onClick={() => setShowModal(true)}
                        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition"
                        title="设置"
                    >
                        ⚙️
                    </button>
                )}
            </div>

            {/* Content */}
            {!isConfigured ? (
                <ConfigureAIButton onConfigure={() => setShowModal(true)} />
            ) : (
                <div className="flex-1 flex flex-col min-h-0">
                    <MessageList messages={messages} isLoading={isPending} />

                    {/* Input Area */}
                    <div className="p-4 border-t border-slate-100 bg-white">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                className="flex-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
                                placeholder="输入您的问题..."
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                                disabled={isPending}
                            />
                            <button
                                onClick={handleSend}
                                disabled={!input.trim() || isPending}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
                            >
                                发送
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {showModal && modalContent}
        </div>
    );
}
